import React, { useState, useEffect, useRef, useCallback } from 'react';
import apiService from './apiService';

const ACCENT = '#F54119';
const CODE_LEN = 6;
const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(s || '').trim());

/**
 * Étape V7 sécurité (2026-10-28) : confirmation du numéro par le code reçu sur WhatsApp
 * (sites web client et chauffeur). Même logique que la feuille des apps natives :
 * envoi automatique à l'ouverture, 6 cases, renvoi après 45 s, repli e-mail (saisie sur place),
 * état de livraison Meta (WhatsApp n'a pas pu livrer → e-mail proposé).
 *
 * Props : open, phone (+243…), purpose ('register' | 'login'), account ('user' | 'driver'),
 *         email / name (facultatifs), bot (champs BotShield à joindre au premier envoi),
 *         onVerified(verification_token), onPhoneTaken(), onClose()
 */
export default function PhoneVerifyModal({ open, phone, purpose = 'register', account = 'user', email, name, bot, onVerified, onPhoneTaken, onClose }) {
  const [channel, setChannel] = useState(null);
  const [hint, setHint] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');
  const [fatal, setFatal] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [askEmail, setAskEmail] = useState(false);
  const [emailInput, setEmailInput] = useState(email || '');
  const [deliveryFailed, setDeliveryFailed] = useState(false);
  const inputRef = useRef(null);
  const sentOnce = useRef(false);

  const send = useCallback(async (useEmail) => {
    setErr(''); setSending(true); setDeliveryFailed(false);
    try {
      // Champs anti-robot : objet (joint au premier envoi seulement, jeton Turnstile à usage unique) ou
      // fonction (connexion par téléphone, 2026-10-30) rappelée à CHAQUE envoi — elle renvoie un jeton frais
      // après avoir réarmé le widget, sinon le renvoi du code était refusé (« Vérification anti-robot échouée »).
      const botNow = typeof bot === 'function' ? (bot() || {}) : (sentOnce.current ? {} : (bot || {}));
      const body = { phone, purpose, account, language: 'fr', name, ...botNow };
      sentOnce.current = true;
      if (useEmail) { body.channel = 'email'; body.email = emailInput.trim(); }
      else if (email) body.email = email;
      const r = await apiService.post('/otp/send', body);
      if (!r?.success) throw r;
      setChannel(r.channel); setHint(r.hint || ''); setResendIn(r.resend_in || 45); setAskEmail(false); setCode('');
      setTimeout(() => inputRef.current?.focus(), 200);
    } catch (e) {
      const c = e?.code || e?.data?.code || null;
      if (c === 'phone_taken') { if (onPhoneTaken) { onPhoneTaken(); return; } setErr('Ce numéro est déjà utilisé par un compte Mbiyo.'); setFatal(true); }
      else if (c === 'cooldown') setErr('Un code vient d\'être envoyé. Patientez quelques secondes.');
      else if (c === 'too_many_requests' || c === 'rate_limited') { setErr('Trop de tentatives. Réessayez plus tard.'); setFatal(true); }
      else if (c === 'no_channel') { setErr('Impossible d\'envoyer le code sur WhatsApp.'); setAskEmail(true); }
      else if (c === 'invalid_email') setErr('Adresse e-mail invalide.');
      else if (c === 'invalid_phone') { setErr('Numéro invalide : 9 chiffres après +243.'); setFatal(true); }
      else setErr(e?.error || e?.message || 'Envoi impossible. Vérifiez votre connexion et réessayez.');
    } finally {
      setSending(false);
    }
  }, [phone, purpose, account, name, email, emailInput, bot, onPhoneTaken]);

  useEffect(() => {
    if (!open) {
      setChannel(null); setHint(''); setCode(''); setErr(''); setFatal(false); setResendIn(0);
      setAskEmail(false); setEmailInput(email || ''); setDeliveryFailed(false); sentOnce.current = false;
      return;
    }
    if (!sentOnce.current) send(false);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || resendIn <= 0) return;
    const id = setInterval(() => setResendIn((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [open, resendIn > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || channel !== 'whatsapp') return;
    let stop = false;
    const id = setInterval(async () => {
      try {
        const r = await apiService.get(`/otp/status?phone=${encodeURIComponent(phone)}`);
        if (!stop && r?.failed) setDeliveryFailed(true);
      } catch (_) {}
    }, 5000);
    return () => { stop = true; clearInterval(id); };
  }, [open, channel, phone]);

  const verify = async (value) => {
    const c = String(value || code).replace(/[^0-9]/g, '');
    if (c.length !== CODE_LEN || verifying) return;
    setErr(''); setVerifying(true);
    try {
      const r = await apiService.post('/otp/verify', { phone, code: c });
      if (!r?.success || !r.verification_token) throw r;
      onVerified && onVerified(r.verification_token);
    } catch (e) {
      setErr((e?.code || e?.data?.code) === 'rate_limited' ? 'Trop de tentatives. Réessayez plus tard.' : 'Code invalide ou expiré. Vérifiez et réessayez.');
      setCode('');
      setTimeout(() => inputRef.current?.focus(), 100);
    } finally {
      setVerifying(false);
    }
  };

  const onChangeCode = (e) => {
    const c = e.target.value.replace(/[^0-9]/g, '').slice(0, CODE_LEN);
    setCode(c);
    if (c.length === CODE_LEN) verify(c);
  };

  if (!open) return null;
  const digits = Array.from({ length: CODE_LEN }, (_, i) => code[i] || '');
  const btn = { background: ACCENT, color: '#fff', border: 'none', borderRadius: 14, padding: '14px 18px', fontWeight: 800, fontSize: 16, width: '100%', cursor: 'pointer' };
  const link = { background: 'none', border: 'none', color: ACCENT, fontWeight: 700, fontSize: 15, cursor: 'pointer', padding: '12px 0', minHeight: 44 };
  const linkMuted = { ...link, color: '#5C636E', fontWeight: 600 };

  return (
    <div role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,.55)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 480, padding: '20px 20px calc(24px + env(safe-area-inset-bottom, 0px))', boxSizing: 'border-box', maxHeight: '92vh', overflowY: 'auto', fontFamily: 'inherit' }}>
        <button type="button" onClick={() => onClose && onClose()} style={linkMuted}>‹ Annuler</button>
        <h2 style={{ margin: '8px 0 8px', fontSize: 22, fontWeight: 800, color: '#1C1C1C' }}>Confirmez votre numéro</h2>
        <p style={{ margin: '0 0 4px', color: '#5C636E', fontSize: 15, lineHeight: 1.4 }}>
          {purpose === 'login'
            ? 'Pour protéger votre compte, confirmez une seule fois votre numéro. Un code à 6 chiffres arrive sur WhatsApp au :'
            : 'Nous envoyons un code à 6 chiffres sur WhatsApp au :'}
        </p>
        <p style={{ margin: '0 0 18px', fontWeight: 700, color: '#1C1C1C', fontSize: 16 }}>{phone}</p>

        {sending && !channel ? <p style={{ color: '#5C636E', textAlign: 'center', padding: '24px 0' }}>Envoi du code…</p> : null}

        {channel && !askEmail ? (
          <>
            <div style={{ background: channel === 'whatsapp' ? '#E7F8EC' : '#EEF2FF', borderRadius: 12, padding: 12, marginBottom: 18, fontSize: 14, color: '#1C1C1C', lineHeight: 1.4 }}>
              {channel === 'whatsapp' ? '💬 Code envoyé sur WhatsApp.' : `✉️ Code envoyé par e-mail à ${hint}`}
              {channel === 'whatsapp' ? <div style={{ color: '#5C636E', fontSize: 12, marginTop: 4 }}>Dans WhatsApp, touchez « Copier le code », revenez ici et collez-le.</div> : null}
            </div>
            <div onClick={() => inputRef.current?.focus()} style={{ display: 'flex', justifyContent: 'space-between', gap: 6, marginBottom: 12, cursor: 'text' }}>
              {digits.map((d, i) => (
                <div key={i} style={{ flex: 1, height: 56, borderRadius: 12, border: `2px solid ${i === code.length ? ACCENT : (d ? '#1C1C1C' : '#DCE0E6')}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 800, color: '#1C1C1C' }}>{d}</div>
              ))}
            </div>
            <input ref={inputRef} value={code} onChange={onChangeCode} inputMode="numeric" autoComplete="one-time-code" maxLength={CODE_LEN} autoFocus
              aria-label="Code à 6 chiffres" style={{ position: 'absolute', opacity: 0, width: 1, height: 1, left: -9999 }} />
            {verifying ? <p style={{ color: '#5C636E', fontSize: 14, textAlign: 'center' }}>Vérification…</p> : null}
            {err ? <p style={{ color: '#B91C1C', fontSize: 14, fontWeight: 600, margin: '0 0 10px' }}>{err}</p> : null}
            {deliveryFailed ? <div style={{ background: '#FFF4E5', color: '#7C2D12', borderRadius: 12, padding: 12, fontSize: 14, marginBottom: 10 }}>WhatsApp n'a pas pu livrer le code à ce numéro. Recevez-le par e-mail.</div> : null}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <button type="button" onClick={() => send(false)} disabled={sending || resendIn > 0 || fatal} style={{ ...link, color: resendIn > 0 || fatal ? '#9AA0A8' : ACCENT }}>
                {resendIn > 0 ? `Renvoyer dans ${resendIn} s` : 'Renvoyer le code'}
              </button>
              {channel === 'whatsapp' && !fatal ? (
                <button type="button" onClick={() => { setErr(''); setAskEmail(true); }} style={linkMuted}>Je n'ai pas WhatsApp / je ne reçois rien</button>
              ) : null}
            </div>
          </>
        ) : null}

        {askEmail ? (
          <div>
            <p style={{ color: '#1C1C1C', fontSize: 15, margin: '0 0 10px' }}>Indiquez une adresse e-mail pour recevoir le code.</p>
            <input type="email" value={emailInput} onChange={(e) => setEmailInput(e.target.value)} placeholder="votre@email.com" autoFocus
              style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #DCE0E6', borderRadius: 12, padding: '13px 14px', fontSize: 16, marginBottom: 12 }} />
            {err ? <p style={{ color: '#B91C1C', fontSize: 14, fontWeight: 600, margin: '0 0 10px' }}>{err}</p> : null}
            <button type="button" onClick={() => (isEmail(emailInput) ? send(true) : setErr('Adresse e-mail invalide.'))} disabled={sending} style={{ ...btn, opacity: sending ? 0.7 : 1 }}>
              {sending ? 'Envoi…' : 'Recevoir le code par e-mail'}
            </button>
            {channel ? <div style={{ textAlign: 'center' }}><button type="button" onClick={() => { setErr(''); setAskEmail(false); }} style={linkMuted}>Saisir le code reçu</button></div> : null}
          </div>
        ) : null}

        {!channel && !sending && !askEmail && err ? (
          <div>
            <p style={{ color: '#B91C1C', fontSize: 14, fontWeight: 600 }}>{err}</p>
            {!fatal ? <button type="button" onClick={() => send(false)} style={btn}>Réessayer</button> : null}
            <div style={{ textAlign: 'center' }}><button type="button" onClick={() => onClose && onClose()} style={linkMuted}>Fermer</button></div>
          </div>
        ) : null}

        <p style={{ color: '#9AA0A8', fontSize: 12, lineHeight: 1.4, marginTop: 20 }}>Ce code prouve que ce numéro est bien le vôtre. Mbiyo ne vous demandera jamais de le partager.</p>
      </div>
    </div>
  );
}
